import { useContextMenu } from '@neo/blocks/context-menu/use-context-menu'
import {
  type KeyboardShortCutItem,
  useKeyboardShortcuts,
} from '@shared/hooks/use-keyboard-shortcuts'
import {
  addEdge,
  type ColorMode,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeMouseHandler,
  type OnConnect,
  type OnConnectEnd,
  type OnConnectStart,
  type OnEdgesDelete,
  type OnNodesDelete,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useUpdateNodeInternals,
} from '@xyflow/react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useParams } from 'router-v6'
import { v4 } from 'uuid'
import { useDarkMode } from 'v2/shared/hooks/use-dark-mode'

import { getContextMenu } from '../config/base.config'
import { MenuNodeID } from '../config/nodes.config'
import { NO_REPLY_HANDLE, VOICEMAIL_DETECTED_INTENT } from '../constants'
import { useFlowQuery } from '../queries'
import { useFlowStore } from '../store'
import {
  type EdgeData,
  FlowNodeTypes,
  type GenericEdge,
  type GenericNode,
  type Intent,
  type NodeDataBase,
} from '../types'
import {
  checkUnsavedChanges,
  clickNode,
  getDefaultNodeData,
  getMenuNodeEdge,
  getNodeVarsForHandle,
} from '../utils'
import { createAndAddIntentToAiNode, isAiAgentOrActionNode } from '../utils/ai-agent-utils'
import { isAiAgentV2Type } from '../utils/ai-agent-v29'
import { useAgentSettings } from './use-agent-settings'
import { FlowHistoryEvent, useFlowHistory, useFlowHistoryManager } from './use-flow-history'
import { useFlowId } from './use-flow-id'
import { useVersionView } from './use-version-view'

export const useFlowProps = () => {
  const { id } = useParams()
  // URL is the source of truth for which version the canvas renders. `?version=<id>`
  // (phlo_connections.id) loads that historical version read-only; absent/invalid = the
  // live editable version. Read through useVersionView (the single, validated parse
  // point) so the canvas never fetches a version that read-only/banner logic rejects.
  const { viewingVersionId } = useVersionView()
  const version = viewingVersionId != null ? String(viewingVersionId) : undefined
  const { isNew } = useFlowId()
  const { data: flowData, ...queryData } = useFlowQuery(id || '', version, isNew)
  const [nodes, setNodes, onNodesChange] = useNodesState<GenericNode>(flowData?.nodes || [])

  const unselectNode = useFlowStore((state) => state.unselectNode)
  const isEditing = useFlowStore((state) => state.isEditing)
  const [edges, setEdges, onEdgesChange] = useEdgesState<GenericEdge>([])
  const [isDarkMode] = useDarkMode()
  const connectingNodeId = useRef<{ nodeId: string | null; handleId: string | null } | null>(null)
  const { screenToFlowPosition, getNodes, addEdges, addNodes, getNode } =
    useReactFlow<Node<NodeDataBase<unknown>>>()
  const { updateEdgeData, deleteElements, updateNodeData } = useReactFlow()
  const removeMenu = useCallback(
    () => deleteElements({ nodes: [{ id: MenuNodeID }], edges: [{ id: MenuNodeID }] }),
    [deleteElements],
  )
  const discardChanges = useFlowStore((state) => state.discardChanges)
  const menuVisiblityRef = useRef(false)
  const updateInternal = useUpdateNodeInternals()
  const selectNode = useFlowStore((state) => state.selectNode)
  const { saveStateToHistory, undo, redo, onUndo, onRedo } = useFlowHistoryManager()
  const setSystemPrompt = useFlowStore((state) => state.setSystemPrompt)

  useAgentSettings({ flowData: flowData ?? undefined })

  useEffect(() => {
    queueMicrotask(() => {
      const currentFlowName = useFlowStore.getState().currentFlowName
      if (flowData?.phlo?.name && !currentFlowName) {
        useFlowStore.getState().setCurrentFlowName(flowData.phlo.name)
      }
    })
  }, [flowData?.phlo?.name, flowData?.phlo?.id])

  // Context menu related code
  const hoveredNodeRef = useRef<GenericNode | null>(null)
  const { menuItems, updateMenu } = useContextMenu<GenericNode>()

  // Attach keyboard shortcuts from menu items
  useKeyboardShortcuts([
    ...(menuItems as KeyboardShortCutItem[]),
    {
      shortcut: { key: 'z', meta: true },
      onlyOnHover: true,
      // Match the toolbar undo/redo buttons: only in edit mode, so a read-only version
      // view (isEditing=false) can't be mutated — or jumped across version boundaries —
      // via the keyboard. (SER-5591)
      method: () => {
        if (useFlowStore.getState().isEditing) undo()
      },
    },
    {
      shortcut: { key: 'y', meta: true },
      onlyOnHover: true,
      method: () => {
        if (useFlowStore.getState().isEditing) redo()
      },
    },
  ])

  useEffect(() => {
    if (flowData) {
      setNodes(flowData.nodes)
      setEdges(flowData.edges)
      useFlowHistory.setState({
        nodes: flowData.nodes,
        edges: flowData.edges,
      })
    }
  }, [flowData, setNodes, setEdges])

  useEffect(() => {
    useFlowHistory.temporal.getState().clear()
    const unsub = onUndo(() => {
      const { nodes: n, edges: e } = useFlowHistory.getState()
      setNodes(n)
      setEdges(e)
    })
    const unsubRedo = onRedo(() => {
      const { nodes: n, edges: e } = useFlowHistory.getState()
      setNodes(n)
      setEdges(e)
    })
    return () => {
      unsub()
      unsubRedo()
    }
  }, [])

  // Viewing a different version swaps the canvas to an unrelated snapshot, which the
  // hydration effect above pushes onto the shared undo stack. Clear that stack on every
  // version change so that after returning to the live version an undo can't pop a
  // historical state onto it (and a save then overwrite the live flow with it). (SER-5591)
  // biome-ignore lint/correctness/useExhaustiveDependencies: clear only when the viewed version changes
  useEffect(() => {
    useFlowHistory.temporal.getState().clear()
  }, [viewingVersionId])

  const onConnect = useCallback<OnConnect>(
    (params) => {
      // reset the start node on connections
      connectingNodeId.current = null

      // Mark user change for editor tracking
      useFlowStore.getState().markUserChange()

      // Feature: Nodevars
      const sourceNode = getNode(params.source) as GenericNode
      let sourceHandle = params.sourceHandle || undefined

      // Create intent for AI nodes if connecting from them
      if (sourceNode && params.source && isAiAgentOrActionNode(sourceNode.type)) {
        const intentId = createAndAddIntentToAiNode(params.source)
        if (intentId) {
          sourceHandle = intentId
        }
        updateInternal(params.source)
      }

      const nodeVars = getNodeVarsForHandle(sourceNode, sourceHandle)
      const edge = { ...params, id: v4(), sourceHandle, data: { nodeVars } }

      setEdges((eds) => addEdge<Edge<EdgeData>>(edge, eds as Edge<EdgeData>[]))
    },
    [setEdges, getNode],
  )

  const onConnectStart = useCallback<OnConnectStart>(
    (_, { nodeId, handleId }) => {
      connectingNodeId.current = { nodeId, handleId }
      menuVisiblityRef.current = false
      removeMenu()
    },
    [setNodes],
  )

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      const type = event.dataTransfer.getData('application/reactflow')

      // check if the dropped element is valid
      if (typeof type === 'undefined' || !type) return

      // TEMPORARY WORKAROUND (v3 edit-lock; remove when the task compiler is re-enabled):
      // read-only flow (e.g. v3 task-runtime, isEditing=false) — block drag-drop node adds.
      if (!isEditing) return

      // Mark user change for editor tracking
      useFlowStore.getState().markUserChange()

      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY })

      const newNode = {
        id: v4(),
        type,
        position,
        data: getDefaultNodeData(type as FlowNodeTypes, getNodes),
      }

      addNodes([newNode])
      clickNode(newNode.id)
    },
    [screenToFlowPosition, addNodes, getNodes, isEditing],
  )

  const onConnectEnd = useCallback<OnConnectEnd>(
    (event) => {
      if (!connectingNodeId.current) return
      const targetIsPane = (event?.target as HTMLDivElement)?.classList?.contains(
        'react-flow__pane',
      )

      if (targetIsPane) {
        // we need to remove the wrapper bounds, in order to get the correct position
        const newNode = {
          id: MenuNodeID,
          position: screenToFlowPosition({
            x: (event as MouseEvent).clientX + 10,
            y: (event as MouseEvent).clientY,
          }),
          type: FlowNodeTypes.menu,
          zIndex: 10,
          data: { meta: { name: 'Menu' }, config: { name: 'Menu' } },
        }

        addNodes([newNode])
        addEdges([getMenuNodeEdge(connectingNodeId.current, MenuNodeID)])

        // This is needed because this event is triggered before the click event
        setTimeout(() => {
          menuVisiblityRef.current = true
        }, 50)
      }
    },
    [screenToFlowPosition, addEdges, addNodes],
  )

  const onClick = useCallback(
    (event: React.MouseEvent) => {
      // Remove the menu if the menu is visible and the click is outside the menu
      if ((event.target as HTMLElement).closest('.edge-popover')) return
      if ((event.target as HTMLElement).closest('#system-prompt-node')) return
      if (menuVisiblityRef.current) removeMenu()
      unselectNode()
    },
    [unselectNode, removeMenu],
  )

  const onNodeClick = useCallback<NodeMouseHandler<Node>>((event, node) => {
    event.stopPropagation()
    const { selectedNodeID, isEditing } = useFlowStore.getState()
    if (selectedNodeID && selectedNodeID !== node.id && isEditing) {
      checkUnsavedChanges(true, () => selectNode(node.id))
    } else {
      selectNode(node.id)
    }
  }, [])

  const onEdgeMouseEnter = useCallback((event: React.MouseEvent, edge: Edge<EdgeData>) => {
    event.stopPropagation()
    updateEdgeData(edge.id, { isHovered: true })
  }, [])

  const onEdgeMouseLeave = useCallback((event: React.MouseEvent, edge: Edge<EdgeData>) => {
    event.stopPropagation()
    updateEdgeData(edge.id, { isHovered: false })
  }, [])

  const onNodeMouseLeave = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    hoveredNodeRef.current = null
  }, [])

  const onNodeMouseEnter = useCallback<NodeMouseHandler<GenericNode>>(
    (event, node) => {
      event.stopPropagation()
      // Only set if the node is hoverable
      if ((event.target as HTMLDivElement).closest('[data-hover]')) {
        hoveredNodeRef.current = node
        updateMenu(getContextMenu(node, hoveredNodeRef, isEditing))
      } else {
        hoveredNodeRef.current = null
        updateMenu([])
      }
    },
    [isEditing],
  )

  const handleNodesChange = useCallback(
    (changes: NodeChange<GenericNode>[]) => {
      onNodesChange(changes)

      if (!changes[0]) return
      if (changes[0].type === 'add' || changes[0].type === 'replace')
        saveStateToHistory(FlowHistoryEvent.NodeAdd)
      else if (changes[0].type === 'remove') saveStateToHistory(FlowHistoryEvent.NodeDelete)
      else if (changes[0].type === 'position') saveStateToHistory(FlowHistoryEvent.NodeChange)
    },
    [onNodesChange, saveStateToHistory],
  )

  const handleEdgesChange = useCallback(
    (changes: EdgeChange<GenericEdge>[]) => {
      onEdgesChange(changes)
      if (!changes[0]) return
      if (changes[0].type === 'add') saveStateToHistory(FlowHistoryEvent.EdgeAdd)
      else if (changes[0].type === 'remove') {
        saveStateToHistory(FlowHistoryEvent.EdgeDelete)
      }
    },
    [onEdgesChange, saveStateToHistory],
  )

  const onEdgesDelete = useCallback<OnEdgesDelete<GenericEdge>>(
    (deletedEdges) => {
      if (deletedEdges.length > 0) {
        useFlowStore.getState().markUserChange()
      }
      deletedEdges.forEach((edge) => {
        if (edge.source && edge.sourceHandle) {
          const sourceNode = getNode(edge.source) as GenericNode
          if (
            sourceNode &&
            (isAiAgentV2Type(sourceNode.type) || sourceNode.type === FlowNodeTypes.ai_action)
          ) {
            const config = sourceNode.data.config as {
              intents?: Intent[]
              voicemail_detection_next_node?: string
              no_reply_next_node?: string
            }
            // The reply-timeout branch hangs off the "no reply" port, not an
            // intent — losing that edge clears the sidebar's dropdown value.
            if (edge.sourceHandle === NO_REPLY_HANDLE) {
              updateNodeData(sourceNode.id, {
                config: { ...config, no_reply_next_node: '' },
              })
              return
            }
            if (config.intents) {
              const deletedIntent = config.intents.find(
                (intent: Intent) => intent.id === edge.sourceHandle,
              )
              const updatedIntents = config.intents.filter(
                (intent: Intent) => intent.id !== edge.sourceHandle,
              )
              const updatedConfig = { ...config, intents: updatedIntents }

              if (deletedIntent?.intent_name === VOICEMAIL_DETECTED_INTENT.name) {
                updatedConfig.voicemail_detection_next_node = ''
              }

              updateNodeData(sourceNode.id, { config: updatedConfig })
            }
          }
        }
      })
    },
    [getNode, updateNodeData],
  )

  const onNodesDelete = useCallback<OnNodesDelete>(
    (deletedNodes) => {
      if (deletedNodes.length > 0) {
        useFlowStore.getState().markUserChange()
      }
      // When deleting an AI agent v2 node, clean up voicemail-connected nodes
      const aiAgentNodes = deletedNodes.filter((node) => isAiAgentV2Type(node.type))
      if (aiAgentNodes.length > 0) {
        const sysPrompt = useFlowStore.getState().systemPrompt
        if (sysPrompt) {
          setSystemPrompt({
            ...sysPrompt,
            ai_nodes: sysPrompt.ai_nodes.filter((node) => !deletedNodes.some((n) => n.id === node)),
          })
        }
        // Delete auto-wired target nodes (marked deletable:false, won't
        // auto-delete): the voicemail branch and the chat reply-timeout branch
        const autoWiredNodeIds: { id: string }[] = []
        for (const aiNode of aiAgentNodes) {
          const config = aiNode.data?.config as { intents?: Intent[] } | undefined
          const vmIntent = config?.intents?.find(
            (i: Intent) => i.intent_name === VOICEMAIL_DETECTED_INTENT.name,
          )
          if (vmIntent) {
            const vmEdge = edges.find(
              (e) => e.source === aiNode.id && e.sourceHandle === vmIntent.id,
            )
            if (vmEdge?.target) {
              autoWiredNodeIds.push({ id: vmEdge.target })
            }
          }
          const noReplyEdge = edges.find(
            (e) => e.source === aiNode.id && e.sourceHandle === NO_REPLY_HANDLE,
          )
          if (noReplyEdge?.target) {
            autoWiredNodeIds.push({ id: noReplyEdge.target })
          }
        }
        if (autoWiredNodeIds.length > 0) {
          const idsToRemove = new Set(autoWiredNodeIds.map((n) => n.id))
          setNodes((prev) => prev.filter((n) => !idsToRemove.has(n.id)))
          setEdges((prev) =>
            prev.filter((e) => !idsToRemove.has(e.source) && !idsToRemove.has(e.target)),
          )
        }
      }
      discardChanges()
    },
    [discardChanges, setSystemPrompt, edges, deleteElements],
  )

  // Note to future devs:
  // This are the handlers that control the whole flow canvas
  // Do not add complex calculations here, only the handlers
  // Keep it simple to enhance the readability and performance
  const returnValue = useMemo(
    () => ({
      onClick,
      onConnect,
      onConnectStart,
      onConnectEnd,
      setNodes,
      setEdges,
      edges,
      onNodesChange: handleNodesChange,
      onEdgesChange: handleEdgesChange,
      nodes,
      removeMenu,
      onDragOver,
      queryData,
      onNodeClick,
      flowData,
      onDrop,
      onEdgeMouseEnter,
      onEdgeMouseLeave,
      onNodeMouseLeave,
      onNodeMouseEnter,
      menuItems,
      onNodesDelete,
      colorMode: (isDarkMode ? 'dark' : 'light') as ColorMode,
      undo,
      redo,
      onEdgesDelete,
    }),
    [edges, nodes, queryData, isDarkMode],
  )

  return returnValue
}
